import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DataGrid, { Column, Lookup, DataGridTypes, Editing } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { GroupItem, SimpleItem, Label, TabbedItem, TabPanelOptions, Tab, FormRef } from "devextreme-react/form";
import IPointEau from "../../models/IPointEau";
import Guid from "../../models/Guid";
import { FormListBase, FormListBaseTab, FormListBaseGrid, FormListBaseToolbar } from '../base';
import useCustomDataSource, { IFormBaseDataSourceOptions, Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import useLookUpBase from '../../components/selectbox/LookupBase';
import GestionSecteurs from '../gestion-secteurs/gestion-secteurs';
import JoursDisponibilite from './gestion-point-eau-jours-disponibilites';
import 'whatwg-fetch';
import IGestionPointEau from '../../models/IGestionPointEau';
import IPointEauStatistique from '../../models/IPointEauStatistique';
import { useContextPopupPage } from '../../components/popups/PopupPage';
import useApi from '../../hooks/useApi';
import GestionEspeces from '../gestion-especes/gestion-especes';
import GestionPointEauType from '../gestion-point-eau-type/gestion-point-eau-type';

interface IGestionPointsEauProps {
  isModeSelection?: boolean,
}

export default function GestionPointsEau({ isModeSelection }: IGestionPointsEauProps) {
  const formBaseDataSourceOptions: IFormBaseDataSourceOptions<IGestionPointEau> = useMemo(() => ({
    path: "pointEau",
    customReadByIdPath: "pointEau/{key}/gestion",
    customSauvegarderPath: "pointEau/save",
    primaryKey: "idPointEau",
  }), []);
  const pointsEauFormDataSource = useCustomDataSource<IPointEau, IGestionPointEau>(formBaseDataSourceOptions);
  const formRef = useRef<FormRef>(null);
  const { getData } = useApi<IPointEauStatistique[]>();
  // const pointsEauFormDataSource = useCustomDataSource<IPointEau, IPointEau>(
  //   {
  //     path: "pointEau",
  //     primaryKey: "idPointEau",
  //     customReadByIdPath: "pointEau/{key}/GetDetailsPointEau"
  //   }
  // );
  const { setMainForm, selectedDataRow: selectedPointEau, onFieldDataChanged, mode, setMode, isFieldModifier, setIsFieldModifier, setSelectedDataRow } = pointsEauFormDataSource;
  const { editorOptions: pointEauTypeEditorOption, PopupGestion: PopupGestionPointEauType } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "pointEauType", showClear: mode !== Mode.Lecture,
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idPointEauType", Guid.empty.toString());
    }
  });
  const { editorOptions: especeEditorOption, PopupGestion: PopupGestionEspece } = useLookUpBase({ isModeModifier: mode !== Mode.Lecture, controllerName: "espece", showClear: mode !== Mode.Lecture });
  const { editorOptions: secteurPecheEditorOption, PopupGestion: PopupGestionsecteurPeche } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "secteur", showClear: mode !== Mode.Lecture,
    titre: "Gestionnaire des secteurs",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idSecteur", Guid.empty.toString());
    }
  })
  const dataGridFilterValue = useMemo(() => ["isDeleted", "anyof", [null, false]], []);
  const { setSelectedValue } = useContextPopupPage();
  const [annee, setAnnee] = useState<number>(new Date().getFullYear());
  const [statistiques, setStatistiques] = useState<IPointEauStatistique[]>([]);
  const [selectedEspeceRowIndex, setSelectedEspeceRowIndex] = useState<number>(-1);

  const loadPointEauStatistiques = useCallback(async (idPointEau?: Guid, year?: number) => {
    if (!idPointEau) {
      setStatistiques([]);
      return;
    }
    const date = new Date(year ?? annee, 0, 1);
    try {
      const stats = await getData({
        customPath: `pointEau/${idPointEau}/StatistiquesPointEau?idEspece=${Guid.empty}&date=${encodeURIComponent(date.toISOString())}`,
        returnOnly: true,
      });
      const resultArray = Array.isArray(stats) ? stats : Object.values(stats ?? {});
      setStatistiques(resultArray as IPointEauStatistique[]);
    } catch {
      setStatistiques([]);
    }
  }, [annee, getData]);

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  });

  useEffect(() => {
    loadPointEauStatistiques(selectedPointEau?.idPointEau, annee);
  }, [selectedPointEau?.idPointEau, annee, loadPointEauStatistiques]);

  useEffect(() => {
    if (mode !== Mode.Lecture) formRef.current?.instance().getEditor("description")?.focus();
  }, [mode])

  const tempsItems = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

  const { descriptionOptions, memviqOptions, pourcentageQuotasOptions, lundiOptions, mardiOptions, mercrediOptions, jeudiOptions, vendrediOptions, samediOptions, dimancheOptions, actifOptions, fermeOptions, disponibleEnLigneOptions, utiliseQuotasOptions, notesOptions, joursDisponibiliteEditorOptions, numberRuleOptions, moneyRuleOptions, poidsLongueurOptions } = useMemo(() => ({
    descriptionOptions: { maxLength: 50 },
    memviqOptions: { maxLength: 25 },
    pourcentageQuotasOptions: {
      inputAttr: { style: 'text-align: right; padding-inline-end: 0px;' },
      width: 70, min: 0, max: 100, showSpinButtons: true
    },
    lundiOptions: { text: "Lundi" },
    mardiOptions: { text: "Mardi" },
    mercrediOptions: { text: "Mercredi" },
    jeudiOptions: { text: "Jeudi" },
    vendrediOptions: { text: "Vendredi" },
    samediOptions: { text: "Samedi" },
    dimancheOptions: { text: "Dimanche" },
    actifOptions: { text: "Actif" },
    fermeOptions: { text: "Fermé" },
    disponibleEnLigneOptions: { text: "Disponible en ligne" },
    utiliseQuotasOptions: { text: "Activer les quotas temporaire" },
    notesOptions: { height: 450 },
    joursDisponibiliteEditorOptions: {
      items: tempsItems,
      defaultValue: tempsItems[0]
    },
    numberRuleOptions: {
      min: 0, max: 9999
    },
    moneyRuleOptions: {
      min: 0, max: 9999,
      //  format: "#0,00"
    },
    poidsLongueurOptions: {
      min: 0, max: 9999,
      format: "#,##0.00"
    }
  }), []);

  const superficieEditorOptions = useMemo(() => {
    return {
      format: { type: 'fixedPoint', precision: 2 },
      inputAttr: { style: 'text-align: right; padding-inline-end: 0px;' }
    };
  }, []);
  const latitudeEditorOptions = useMemo(() => {
    return {
      format: { type: 'fixedPoint', precision: 6 },
      inputAttr: { style: 'text-align: right; padding-inline-end: 0px;' }
    };
  }, []);
  const longitudeEditorOptions = useMemo(() => {
    return {
      format: { type: 'fixedPoint', precision: 6 },
      inputAttr: { style: 'text-align: right; padding-inline-end: 0px;' }
    };
  }, []);
  const anneeEditorOptions = useMemo(() => {
    return { min: 1970, showSpinButtons: true, width: 85, defaultValue: new Date().getFullYear() };
  }, []);

  const masseQuotasCellRender = useCallback((e: DataGridTypes.ColumnCellTemplateData) => {
    return (
      <span>{e.data.masseMesuree} / {e.data.quotaMasse} </span>
    )

  }, [])

  const quantiteQuotas = useCallback((e: DataGridTypes.ColumnCellTemplateData) => {
    return (
      <span>{e.data.nombreCaptures} / {e.data.quotaQuantite} </span>
    )

  }, [])

  const setDataRow = (e: string) => {
    setSelectedDataRow((prev: IGestionPointEau) => (
      { ...prev, [e]: Guid.empty.toString() }
    ));
    setIsFieldModifier(true);
  }

  const onChexBoxChange = useCallback((value: number) => {
    if (mode !== Mode.Lecture) {
      setIsFieldModifier(true)
      setSelectedDataRow((prevState) => {
        return {
          ...prevState,
          joursDisponibilite: value
        }
      })
    }
  }, [mode])


  const onSelectionChanged = useCallback(({ selectedRowsData, selectedRowKeys }: DataGridTypes.SelectionChangedEvent) => {
    if (selectedRowsData.length > 0) {
      const data = selectedRowsData[0];
      const rowIndex = selectedPointEau.pointEauEspeces.findIndex(item => item === data);
      console.log('Selected row:', data);
      console.log('Selected row index:', rowIndex);
      console.log('Selected row keys:', selectedRowKeys);
      setSelectedEspeceRowIndex(rowIndex);
    } else {
      setSelectedEspeceRowIndex(-1);
    }
  }, [selectedPointEau.pointEauEspeces]);

  return (
    <FormListBase data={pointsEauFormDataSource}>
      <FormListBaseGrid
        dataSource={pointsEauFormDataSource.dataSource}
        onFocusedRowChanged={(e: any) => {
          if (mode === Mode.Modifier) {
            setMode(Mode.Lecture)
          }
          if (setSelectedValue && e.row?.data) {
            setSelectedValue(e.row.data);
          }
        }}
      >
        <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
        <Column key={"grid-col-type-point-eau"} dataField={"idPointEauType"} caption="Type" allowHiding={false}>
          <Lookup {...pointEauTypeEditorOption} /></Column>
        <Column key={"grid-col-id-secteur"} dataField={"idSecteur"} caption="Secteur" allowHiding={false}>
          <Lookup {...secteurPecheEditorOption} />
        </Column>
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`${selectedPointEau?.description || ''}`} modifierOptions={{ disabled: !isFieldModifier }} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} />
      {!isModeSelection || isModeSelection && mode !== Mode.Lecture
        ? <FormListBaseTab>
          <PopupGestionEspece
            onSelectionchanged={(data: any) => {
              setIsFieldModifier(true);
              console.log('Selected species:', data);

              console.log('Selected species:', data);
              console.log('Selected row index:', selectedEspeceRowIndex);

              if (selectedEspeceRowIndex >= 0) {
                setSelectedDataRow((prevState) => {
                  const updatedEspeces = [...prevState.pointEauEspeces];
                  if (updatedEspeces[selectedEspeceRowIndex]) {
                    updatedEspeces[selectedEspeceRowIndex] = {
                      ...updatedEspeces[selectedEspeceRowIndex],
                      idEspece: data.idEspece
                    };
                  }
                  console.log('Updated especes:', updatedEspeces);
                  return {
                    ...prevState,
                    pointEauEspeces: updatedEspeces
                  };
                });
              } else {
                console.log('No row selected. Please click on a row first.');
              }
            }}
          >
            <GestionEspeces isModeSelection={true} />
          </PopupGestionEspece>
          <PopupGestionPointEauType
            onSelectionchanged={(data: any) => {
              setIsFieldModifier(true);
              formRef.current?.instance().updateData("idPointEauType", data.idPointEauType);
            }}
          >
            <GestionPointEauType isModeSelection={true} />
          </PopupGestionPointEauType>
          <PopupGestionsecteurPeche
            onSelectionchanged={(data: any) => {
              setIsFieldModifier(true);
              formRef.current?.instance().updateData("idSecteur", data.idSecteur);
            }}
          >
            <GestionSecteurs isModeSelection={true} />
          </PopupGestionsecteurPeche>
          <TabItem title="Informations">
            {/* <div style={{ display: "flex" }}> */}
            <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedPointEau || {} as IPointEau} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
              <GroupItem colCount={5}>
                <GroupItem colSpan={3}>
                  <GroupItem colCount={2}>
                    <GroupItem>
                      <SimpleItem key={"frm-description"} dataField={"description"}
                        editorOptions={descriptionOptions} />
                      <SimpleItem key={"frm-type-point-eau"} dataField={"idPointEauType"} editorType={"dxSelectBox"}
                        editorOptions={pointEauTypeEditorOption}>
                        <Label text={"Type de point d'eau"} />
                      </SimpleItem>
                      <SimpleItem key={"frm-id-secteur"} dataField={"idSecteur"} editorOptions={secteurPecheEditorOption} editorType={"dxSelectBox"}>
                        <Label text={"Secteur de pêche"} />
                      </SimpleItem>
                      <SimpleItem key={"frm-superficie"} dataField={"superficie"} editorOptions={superficieEditorOptions} />
                      <SimpleItem key={"frm-latitude"} dataField={"latitude"} editorOptions={latitudeEditorOptions} />
                      <SimpleItem key={"frm-longitude"} dataField={"longitude"} editorOptions={longitudeEditorOptions} />
                      <SimpleItem key={"frm-memviq"} dataField={"memviq"}
                        editorOptions={memviqOptions}>
                        <Label text={"Numéro de MEMVIQ"} />
                      </SimpleItem>
                      <GroupItem alignItemLabels={false}>
                        <SimpleItem key={"frm-pourcentage-avertissement-quotas"} dataField={"pourcentageAvertissementQuotas"} editorType={"dxNumberBox"}
                          editorOptions={pourcentageQuotasOptions}>
                          <Label text={"% avertissement quotas"} />
                        </SimpleItem>
                      </GroupItem>
                    </GroupItem>
                    <GroupItem colCount={2}>
                      <GroupItem caption={"Jours disponibles"}>
                        <SimpleItem key={"frm-Jours-disponibles"}
                          render={() =>
                            <JoursDisponibilite
                              pointEauSelected={selectedPointEau.joursDisponibilite}
                              isModeModifier={mode === Mode.Lecture}
                              onChexBoxChange={(value: number) => onChexBoxChange(value)}
                            />
                          }
                        >
                          <Label visible={false} />
                        </SimpleItem>
                      </GroupItem>
                      <GroupItem caption={"Statut"}>
                        <SimpleItem key={"frm-actif"} dataField={"actif"} editorType={"dxCheckBox"}
                          editorOptions={actifOptions}
                        >
                          <Label visible={false} />
                        </SimpleItem>
                        <SimpleItem key={"frm-fermé"} dataField={"ferme"} editorType={"dxCheckBox"}
                          editorOptions={fermeOptions}
                        >
                          <Label visible={false} />
                        </SimpleItem>
                        <SimpleItem key={"frm-disponible-en-ligne"} dataField={"disponibleEnLigne"} editorType={"dxCheckBox"}
                          editorOptions={disponibleEnLigneOptions}
                        >
                          <Label visible={false} />
                        </SimpleItem>
                        <SimpleItem key={"frm-utilise-quotas-temporaire"} dataField={"utiliseQuotaTemporaire"} editorType={"dxCheckBox"}
                          editorOptions={utiliseQuotasOptions}
                        >
                          <Label visible={false} />
                        </SimpleItem>
                      </GroupItem>
                    </GroupItem>
                  </GroupItem>
                  <TabbedItem>
                    <TabPanelOptions height={"100%"} />
                    <Tab title={"Espèces"}>
                      <GroupItem>
                        <DataGrid
                          showBorders={true}
                          allowColumnResizing={true}
                          dataSource={selectedPointEau.pointEauEspeces}
                          allowColumnReordering={true}
                          // selection={{ mode: 'single' }}
                          // keyExpr="idPointEauEspece"
                          // onSelectionChanged={onSelectionChanged}
                          onInitNewRow={(e: any) => {
                            e.data.idPointEau = selectedPointEau.idPointEau;
                            e.data.isDeleted = false;
                          }}
                          filterValue={dataGridFilterValue}
                          height={"100%"}
                          noDataText={"Aucune espèce"}
                          onEditorPreparing={(e: any) => {
                            // if (e.dataField === "idPermisType") {
                            const originalOnValueChanged = e.editorOptions.onValueChanged;
                            e.editorOptions.onValueChanged = (args: any) => {
                              if (originalOnValueChanged) {
                                originalOnValueChanged(args);
                              }

                              const currentData = e.component.option("dataSource");
                              const rowIndex = e.rowIndex;
                              const rowData = currentData[rowIndex];

                              const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                              setSelectedDataRow(prev => ({
                                ...prev,
                                pointEauEspeces: updatedData
                              }));
                              setIsFieldModifier(true);
                            };
                            // }
                          }}
                          onRowRemoving={async (e) => {
                            e.cancel = true;
                            // if (e?.data?.idPermisType) {
                            const index = selectedPointEau.pointEauEspeces.findIndex(item =>
                              item.idPointEauEspece?.equals(e.data.idPointEauEspece)
                            );
                            if (index !== -1) {
                              const updatedData = [...selectedPointEau.pointEauEspeces];
                              updatedData[index] = { ...updatedData[index], isDeleted: true };
                              setSelectedDataRow(prev => ({
                                ...prev,
                                pointEauEspeces: updatedData
                              }));
                              setIsFieldModifier(true);
                            }
                            // }
                          }}
                        >
                          <Editing
                            allowAdding={mode !== Mode.Lecture}
                            allowDeleting={mode !== Mode.Lecture}
                            allowUpdating={mode !== Mode.Lecture}
                            newRowPosition={"first"}
                            mode={"cell"}
                            startEditAction={"click"}
                          />
                          <Column caption={"Description"}>
                            <Column
                              // editorOptions={especeEditorOption}
                              caption={"Nom d'espèce"} dataField='idEspece' width={'50%'}>
                              <Lookup {...especeEditorOption} />
                            </Column>
                          </Column>
                          <Column caption={"Limite de prise"}>
                            <Column caption={"Réservation"} dataField='limiteReservation' dataType='number' editorOptions={numberRuleOptions} />
                            <Column caption={"Quotidien"} dataField='limiteQuotidien' dataType='number' editorOptions={numberRuleOptions} />
                            <Column caption={"Limite de possession"} dataField='limitePossession' dataType='number' editorOptions={numberRuleOptions} />
                          </Column>
                          <Column dataField="isDeleted" dataType="boolean" visible={false} />
                        </DataGrid>
                      </GroupItem>
                    </Tab>
                    <Tab title={"Ensemensement"}>
                      <GroupItem>
                        <DataGrid
                          showBorders={true}
                          allowColumnResizing={true}
                          dataSource={selectedPointEau.pointEauEnsemensements}
                          onInitNewRow={(e: any) => {
                            e.data.idPointEau = selectedPointEau.idPointEau;
                            e.data.isDeleted = false;
                          }}
                          filterValue={dataGridFilterValue}
                          height={"100%"}
                          noDataText={"Aucune ensemensement"}
                          onEditorPreparing={(e: any) => {
                            // if (e.dataField === "idPermisType") {
                            const originalOnValueChanged = e.editorOptions.onValueChanged;
                            e.editorOptions.onValueChanged = (args: any) => {
                              if (originalOnValueChanged) {
                                originalOnValueChanged(args);
                              }

                              const currentData = e.component.option("dataSource");
                              const rowIndex = e.rowIndex;
                              const rowData = currentData[rowIndex];

                              const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                              setSelectedDataRow(prev => ({
                                ...prev,
                                pointEauEnsemensements: updatedData
                              }));
                              setIsFieldModifier(true);
                            };
                            // }
                          }}
                          onRowRemoving={async (e) => {
                            e.cancel = true;
                            // if (e?.data?.idPermisType) {
                            const index = selectedPointEau.pointEauEnsemensements.findIndex(item =>
                              item.idPointEauEnsemensement?.equals(e.data.idPointEauEnsemensement)
                            );
                            if (index !== -1) {
                              const updatedData = [...selectedPointEau.pointEauEnsemensements];
                              updatedData[index] = { ...updatedData[index], isDeleted: true };
                              setSelectedDataRow(prev => ({
                                ...prev,
                                pointEauEnsemensements: updatedData
                              }));
                              setIsFieldModifier(true);
                            }
                            // }
                          }}
                        >
                          <Editing
                            allowAdding={mode !== Mode.Lecture}
                            allowDeleting={mode !== Mode.Lecture}
                            allowUpdating={mode !== Mode.Lecture}
                            newRowPosition={"first"}
                            mode={"cell"}
                            startEditAction={"click"}
                          />
                          <Column caption={"Espèce"} dataField='idEspece'>
                            <Lookup {...especeEditorOption} />
                          </Column>
                          <Column caption={"Date"} dataField='date' dataType='date' format="dd-MM-yyyy" />
                          <Column caption={"Âge"} dataField='age' dataType='number' editorOptions={numberRuleOptions} />
                          <Column caption={"Quantité"} dataField='quantite' dataType='number' editorOptions={numberRuleOptions} />
                          <Column caption={"Poids totaux"} dataField='poidsTotaux' dataType='number' editorOptions={poidsLongueurOptions} />
                          <Column caption={"Longueur"} dataField='longueur' dataType='number' editorOptions={poidsLongueurOptions} />
                          <Column caption={"Fournisseur"} dataField='nomFournisseur' dataType='string' />
                          <Column caption={"Montant"} dataField='montant' dataType='number' editorOptions={moneyRuleOptions} />
                          <Column dataField="isDeleted" dataType="boolean" visible={false} />
                        </DataGrid>
                      </GroupItem>
                    </Tab>
                    <Tab title={"Quotas du point d'eau"}>
                      <GroupItem>
                        <DataGrid
                          showBorders={true}
                          allowColumnResizing={true}
                          dataSource={selectedPointEau.pointEauQuotas}
                          onInitNewRow={(e: any) => {
                            e.data.idPointEau = selectedPointEau.idPointEau;
                            e.data.isDeleted = false;
                          }}
                          filterValue={dataGridFilterValue}
                          height={"100%"}
                          noDataText={"Aucune quotas"}
                          onEditorPreparing={(e: any) => {
                            // if (e.dataField === "idPermisType") {
                            const originalOnValueChanged = e.editorOptions.onValueChanged;
                            e.editorOptions.onValueChanged = (args: any) => {
                              if (originalOnValueChanged) {
                                originalOnValueChanged(args);
                              }

                              const currentData = e.component.option("dataSource");
                              const rowIndex = e.rowIndex;
                              const rowData = currentData[rowIndex];

                              const updatedData = currentData.filter((_: any, index: any) => index !== rowIndex);
                              setSelectedDataRow(prev => ({
                                ...prev,
                                pointEauQuotas: updatedData
                              }));
                              setIsFieldModifier(true);
                            };
                            // }
                          }}
                          onRowRemoving={async (e) => {
                            e.cancel = true;
                            // if (e?.data?.idPermisType) {
                            const index = selectedPointEau.pointEauQuotas.findIndex(item =>
                              item.idPointEauQuota?.equals(e.data.idPointEauQuota)
                            );
                            if (index !== -1) {
                              const updatedData = [...selectedPointEau.pointEauQuotas];
                              updatedData[index] = { ...updatedData[index], isDeleted: true };
                              setSelectedDataRow(prev => ({
                                ...prev,
                                pointEauQuotas: updatedData
                              }));
                              setIsFieldModifier(true);
                            }
                            // }
                          }}
                        >
                          <Editing
                            allowAdding={mode !== Mode.Lecture}
                            allowDeleting={mode !== Mode.Lecture}
                            allowUpdating={mode !== Mode.Lecture}
                            newRowPosition={"first"}
                            mode={"cell"}
                            startEditAction={"click"}
                          />
                          <Column caption={"Espèce"} dataField='idEspece'>
                            <Lookup {...especeEditorOption} />
                          </Column>
                          <Column caption={"Quotas poid"} dataField='poids' dataType='number' editorOptions={moneyRuleOptions} format=",##0.00" />
                          <Column caption={"Quotas Quantité"} dataField='quantite' dataType='number' editorOptions={numberRuleOptions} />
                          <Column caption={"Date d'ouverture"} dataField='dateOuverture' dataType='date' format="dd-MM-yyyy" />
                          <Column caption={"Date de fermeture"} dataField='dateFermeture' dataType='date' format="dd-MM-yyyy" />
                          <Column dataField="isDeleted" dataType="boolean" visible={false} />
                        </DataGrid>
                      </GroupItem>
                    </Tab>
                  </TabbedItem>
                </GroupItem>
                <GroupItem caption={"Notes"} colSpan={2}>
                  <SimpleItem key={"frm-notes"} dataField={"notes"} editorType={"dxTextArea"}
                    editorOptions={notesOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                </GroupItem>
              </GroupItem>
            </Form>
            {/* </div> */}
          </TabItem>
          <TabItem title="Statistiques">
            <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} formData={statistiques || [] as IPointEauStatistique[]} onFieldDataChanged={onFieldDataChanged}>
              <SimpleItem key={"frm-annee"} editorType={"dxNumberBox"}
                editorOptions={{
                  ...anneeEditorOptions,
                  value: annee,
                  onValueChanged: (e: any) => setAnnee(e.value ?? new Date().getFullYear())
                }}
              >
                <Label text={"Année"} />
              </SimpleItem>
              <SimpleItem>
                <label>Note: La masse est estimé selon la masse moyenne des prises</label>
              </SimpleItem>
              <GroupItem>
                <DataGrid showBorders={true} allowColumnResizing={true}
                  dataSource={statistiques}
                  key={'data-grid-point-eau'}>
                  <Column caption={"Espèce"} dataField='idEspece'>
                    <Lookup {...especeEditorOption} />
                  </Column>
                  <Column dataType='object' caption={"Masse / Quotas"} cellRender={masseQuotasCellRender} />
                  <Column dataType='object' caption={"Quantité / Quotas"} cellRender={quantiteQuotas} />
                </DataGrid>
              </GroupItem>
            </Form>
          </TabItem>
          <TabItem title="Système"><FormSysteme formData={selectedPointEau || {} as IPointEau} /></TabItem>
        </FormListBaseTab> : null}
    </FormListBase>
  );
}
